import { useMemo } from 'react';
import { NAV_LABEL_DEFAULTS } from '@l2u/shared/brandingDefaults';
import type { AuditSubjectKind } from '@l2u/shared/schemas';
import { useBranding } from './branding.tsx';
import type { Branding } from './types.ts';

// The entity nouns a tenant may rename (spec 2026-08-30, §4). One resolver for
// both apps: admin and the contractor portal must never disagree about what a
// tenant calls their counterparties.
//
// Singulars and plurals are resolved INDEPENDENTLY. There is no rule that turns
// an arbitrary tenant plural back into a singular — "Personnel", "Crew",
// "Fleet staff" all defeat a trailing-s heuristic — so branding stores both and
// an unset key falls back to its own default rather than to its partner.
export type EntityLabels = {
  company: string;
  companies: string;
  driver: string;
  drivers: string;
};

/**
 * The labels to render for a tenant: their override where one is set and
 * non-blank, the built-in default otherwise.
 *
 * Blank counts as unset, matching `resolveNavLabel`
 * (`apps/admin/src/lib/adminNav.ts`): Settings clears an override by emptying
 * its input, and a whitespace-only label would otherwise render as nothing at
 * all in a heading or a table header.
 *
 * Pure, so page code can be tested without a branding provider — components
 * should use `useEntityLabels()`, and pure helpers should take an
 * `EntityLabels` argument rather than importing the hook.
 */
export function resolveEntityLabels(branding: Partial<Branding> | undefined): EntityLabels {
  const pick = (key: keyof EntityLabels): string => {
    const override = branding?.navLabels?.[key];
    if (typeof override === 'string' && override.trim().length > 0) return override;
    return NAV_LABEL_DEFAULTS[key];
  };
  return {
    company: pick('company'),
    companies: pick('companies'),
    driver: pick('driver'),
    drivers: pick('drivers'),
  };
}

/** `resolveEntityLabels` against the branding already in context. */
export function useEntityLabels(): EntityLabels {
  const { branding } = useBranding();
  return useMemo(() => resolveEntityLabels(branding), [branding]);
}

/**
 * The built-in labels, for pure helpers whose callers may not have branding to
 * hand (tests, and the optional-argument defaults in `activityFormat`).
 * Identical to `resolveEntityLabels(undefined)`, without the allocation.
 */
export const ENTITY_LABEL_DEFAULTS: EntityLabels = { ...NAV_LABEL_DEFAULTS };

/** The noun for an audit's subject kind, in the tenant's own vocabulary. */
export function auditSubjectKindLabel(kind: AuditSubjectKind, labels: EntityLabels): string {
  switch (kind) {
    case 'asset':
      return 'Asset';
    case 'driver':
      return labels.driver;
    case 'company':
      return labels.company;
  }
}

type AuditSubjectEmbeds = {
  subject_kind: AuditSubjectKind;
  asset?: {
    make: string | null;
    model: string | null;
    year: number | null;
    registration_plate: string | null;
  } | null;
  driver?: { name: string } | null;
  company?: { company_name: string } | null;
};

/**
 * One line naming what an audit was run against — the card title on My
 * Audits, the runner subtitle, the corrective-action row. The asset rule is
 * MyAudits.tsx's long-standing year/make/model → plate → 'Asset'; the driver
 * and company fallbacks are the tenant's nouns, never a hard-coded word, so a
 * tenant that renamed drivers to "Couriers" does not see "Driver" here.
 * Pure: the API twin (auditSubjectDisplay, apps/api/src/lib/auditSubject.ts)
 * has no labels to hand, so it uses lower-case built-in nouns for email copy.
 * The two DIVERGE ON PURPOSE and only in that: capitalised 'Asset' here, and
 * tenant nouns instead of 'driver'/'company'. The asset chain itself must stay
 * character-identical to the twin's — the same audit has to read the same in
 * the runner as in its failure email, so treat a difference there as drift to
 * fix rather than a local choice.
 */
export function auditSubjectLabel(audit: AuditSubjectEmbeds, labels: EntityLabels): string {
  switch (audit.subject_kind) {
    case 'asset':
      return (
        [audit.asset?.year, audit.asset?.make, audit.asset?.model].filter(Boolean).join(' ') ||
        audit.asset?.registration_plate ||
        'Asset'
      );
    case 'driver':
      return audit.driver?.name || labels.driver;
    case 'company':
      return audit.company?.company_name || labels.company;
  }
}
